/**
 * Product Dev pipeline hooks — capsule-order + record-link procedures.
 *
 * Thin wrappers over `trpc.productDev.*`, mirroring the pattern in
 * useProductDev.ts. Kept as a separate file because it groups the capsule
 * (CapsuleOrderDialog) and record-link (RelatedRecordsCard) surface, which is
 * conceptually distinct from the core record CRUD in useProductDev.ts.
 *
 * These procedures used to be behind a single `unknown` cast while a parallel
 * backend PR landed; now that `addCapsuleToPurchaseOrder` / `searchRecords` /
 * `listLinks` / `createLink` / `deleteLink` are live on the real AppRouter,
 * every hook below calls `trpc.productDev.*` directly.
 */
import { trpc, type RouterOutputs } from '@/lib/trpc';

/** Backend wire shape for one linked record (product-dev.types.ts ProductDevLinkDto). */
type ProductDevLinkWire = RouterOutputs['productDev']['listLinks'][number];

export type RecordLinkType = ProductDevLinkWire['link_type'];

/** One linked record, resolved to the OTHER side of the relationship (links are
 *  bidirectional; the backend returns whichever record isn't the one queried).
 *  Flat component-facing shape — mapped from the backend's nested snake_case
 *  wire shape in `useRecordLinks`, so components never see the wire. */
export interface RecordLink {
  /** The link row id — the handle `deleteLink` keys off. */
  id: string;
  otherId: string;
  otherStyleName: string;
  otherStyleCode: string | null;
  /** Signed hero thumbnail of the linked record (list-view fallback chain). */
  otherHeroUrl: string | null;
  linkType: RecordLinkType;
  note: string | null;
}

const mapLink = (w: ProductDevLinkWire): RecordLink => ({
  id: w.id,
  otherId: w.other.id,
  otherStyleName: w.other.style_name,
  otherStyleCode: w.other.style_code,
  otherHeroUrl: w.other.hero_image_url,
  linkType: w.link_type,
  note: w.note,
});

/** Narrow, component-facing search result — RelatedRecordsCard's picker only
 *  ever reads these three fields, even though the real DTO also carries
 *  `stage`/`record_type`. */
export interface SearchRecordResult {
  id: string;
  style_name: string;
  style_code: string | null;
}

// ---- Exported hooks --------------------------------------------------------

export function useAddCapsuleToPurchaseOrder() {
  const utils = trpc.useUtils();
  return trpc.productDev.addCapsuleToPurchaseOrder.useMutation({
    onSuccess: () => {
      void utils.productDev.list.invalidate();
      void utils.purchaseOrders.getOrders.invalidate();
    },
  });
}

export function useAddSelectionToPurchaseOrder() {
  const utils = trpc.useUtils();
  return trpc.productDev.addSelectionToPurchaseOrder.useMutation({
    onSuccess: () => {
      void utils.productDev.list.invalidate();
      void utils.purchaseOrders.getOrders.invalidate();
    },
  });
}

/** Record search for the link picker. Fires at >= 2 chars, keeps the prior page
 *  visible while the next keystroke resolves (mirrors InlineProductSearch). */
export function useSearchRecords(search: string, enabled: boolean) {
  const trimmed = search.trim();
  return trpc.productDev.searchRecords.useQuery(
    { search: trimmed },
    {
      enabled: enabled && trimmed.length >= 2,
      staleTime: 30_000,
      placeholderData: (prev) => prev,
    },
  );
}

export function useRecordLinks(productDevId: string, enabled = true) {
  const q = trpc.productDev.listLinks.useQuery(
    { productDevId },
    { enabled, staleTime: 30_000 },
  );
  return { ...q, data: q.data?.map(mapLink) };
}

export function useCreateLink() {
  return trpc.productDev.createLink.useMutation();
}

export function useDeleteLink() {
  return trpc.productDev.deleteLink.useMutation();
}
